import React, { createContext, useState, useContext, ReactNode, useCallback, useRef, useMemo } from 'react';
import { hasCustomAdventures } from '@/utils/homepageApi';

interface HomePageContextType {
  businessesWithCustomAdventures: Set<string>;
  checkForCustomAdventures: (businessId: string) => Promise<boolean>;
  isLoadingCustomAdventures: boolean;
  preloadCustomAdventuresForBusinesses: (businessIds: string[]) => void;
  checkedBusinesses: Set<string>;
}

const HomePageContext = createContext<HomePageContextType | undefined>(undefined);

export const useHomePage = (): HomePageContextType => {
  const context = useContext(HomePageContext);
  if (!context) {
    throw new Error('useHomePage must be used within a HomePageProvider');
  }
  return context;
};

interface HomePageProviderProps {
  children: ReactNode;
}

export const HomePageProvider: React.FC<HomePageProviderProps> = ({ children }) => {
  const [businessesWithCustomAdventures, setBusinessesWithCustomAdventures] = useState<Set<string>>(new Set());
  const [isLoadingCustomAdventures, setIsLoadingCustomAdventures] = useState(false);
  const [checkedBusinesses, setCheckedBusinesses] = useState<Set<string>>(new Set());

  const pendingChecks = useRef<Set<string>>(new Set());
  const batchTimeout = useRef<NodeJS.Timeout | null>(null);

  const checkForCustomAdventures = useCallback(async (businessId: string): Promise<boolean> => {
    if (!businessId) {
      console.error('[HomePageContext] checkForCustomAdventures called with empty businessId');
      return false;
    }

    if (businessesWithCustomAdventures.has(businessId)) return true;
    if (checkedBusinesses.has(businessId)) return false;

    if (pendingChecks.current.has(businessId)) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!pendingChecks.current.has(businessId)) {
            clearInterval(checkInterval);
            resolve(businessesWithCustomAdventures.has(businessId));
          }
        }, 100);
      });
    }

    pendingChecks.current.add(businessId);

    try {
      const hasAdventures = await hasCustomAdventures(businessId);

      setCheckedBusinesses(prev => new Set(prev).add(businessId));

      if (hasAdventures) {
        setBusinessesWithCustomAdventures(prev => new Set(prev).add(businessId));
      }

      return hasAdventures;
    } catch (error) {
      console.error('[HomePageContext] Error checking for custom adventures:', {
        businessId,
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    } finally {
      pendingChecks.current.delete(businessId);
    }
  }, [businessesWithCustomAdventures, checkedBusinesses]);

  const preloadCustomAdventuresForBusinesses = useCallback((businessIds: string[]) => {
    const uncheckedIds = businessIds.filter(id =>
      !checkedBusinesses.has(id) &&
      !pendingChecks.current.has(id) &&
      !businessesWithCustomAdventures.has(id)
    );

    if (uncheckedIds.length === 0) return;

    if (batchTimeout.current) {
      clearTimeout(batchTimeout.current);
    }

    setIsLoadingCustomAdventures(true);

    const processBatch = async () => {
      try {
        const batchPromises = uncheckedIds.map(async (businessId) => {
          if (!pendingChecks.current.has(businessId)) {
            pendingChecks.current.add(businessId);
            try {
              const hasAdventures = await hasCustomAdventures(businessId);
              return { businessId, hasAdventures };
            } finally {
              pendingChecks.current.delete(businessId);
            }
          }
          return null;
        });

        const results = await Promise.all(batchPromises);
        const validResults = results.filter(result => result !== null) as Array<{ businessId: string; hasAdventures: boolean }>;

        setCheckedBusinesses(prev => {
          const newSet = new Set(prev);
          validResults.forEach(result => newSet.add(result.businessId));
          return newSet;
        });

        setBusinessesWithCustomAdventures(prev => {
          const newSet = new Set(prev);
          validResults.filter(r => r.hasAdventures).forEach(result => newSet.add(result.businessId));
          return newSet;
        });
      } catch (error) {
        console.error('[HomePageContext] Error in batch processing custom adventures:', error);
      } finally {
        setIsLoadingCustomAdventures(false);
      }
    };

    batchTimeout.current = setTimeout(processBatch, 300);
  }, [checkedBusinesses, businessesWithCustomAdventures]);

  const contextValue = useMemo(() => ({
    businessesWithCustomAdventures,
    checkForCustomAdventures,
    isLoadingCustomAdventures,
    preloadCustomAdventuresForBusinesses,
    checkedBusinesses
  }), [
    businessesWithCustomAdventures,
    checkForCustomAdventures,
    isLoadingCustomAdventures,
    preloadCustomAdventuresForBusinesses,
    checkedBusinesses
  ]);

  return (
    <HomePageContext.Provider value={contextValue}>
      {children}
    </HomePageContext.Provider>
  );
};
